/**
 * API接続の基本型定義
 */

// Laravel APIのベースURL
export const API_BASE_URL = '/api';

// 共通のAPIレスポンス型
export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
  errors?: Record<string, string[]>;
}

// SaaS 課金プラン情報 (GET /api/saas/plan)。来店会計の plan 系とは別物
export interface SaasPlanInfo {
  // false の間はバナー/設定タブを出さない (店舗と合意前のロールアウト制御)
  ui_enabled: boolean;
  plan: 'trial' | 'monitor' | 'free' | 'light' | 'standard' | 'pro';
  plan_label: string;
  trial_kind: 'trial' | 'monitor' | null;
  trial_ends_at: string | null;
  days_remaining: number | null;
  monitor_discount: boolean;
  monitor_discount_eligible_until: string | null;
  usage: {
    stores: number;
    users: number;
  };
}

// プランによる機能制限の状態 (GET /api/saas/features。ログインしていれば誰でも。2026-10-15)
// 機能のキーは BE の config/saas_plans.php の features と揃える（金額の記録に触れる機能は運営判断待ちで未配線）
export type SaasFeatureKey = 'line_notify' | 'multi_store';

export interface SaasLockedFeature {
  key: SaasFeatureKey;
  label: string;
  required_plan: string | null;
}

export interface SaasFeaturesInfo {
  // false なら何もロックしていない (全体スイッチ OFF か、会社の表示フラグ OFF)
  enforced: boolean;
  plan: string | null;
  locked: SaasLockedFeature[];
  // 店舗数の上限 (null = 上限なし)
  store_limit: number | null;
  // 会社のオーナーか (ロック時の案内を「プランを見る」/「オーナーにご相談ください」で出し分ける)
  is_owner: boolean;
}

// 共通のAPIエラーレスポンス型
export interface ApiErrorResponse {
  success: false;
  message: string;
  errors?: Record<string, string[]>;
  status?: number;
}

// ログイン用のレスポンス型
export interface LoginResponse {
  message: string;
  user: {
    id: number;
    email: string | null;
    email_verified_at: string | null;
    company_id: number | null;
    user_cd: string | null;
    line_id?: string | null;
    auth_provider?: string;
    line_display_name?: string | null;
    employee?: {
      id: number;
      display_name: string;
      role_type: 'ADMIN' | 'STAFF' | 'CAST';
      stage_name?: string | null;
      is_admin?: boolean;
      is_staff?: boolean;
      is_cast?: boolean;
    };
  };
  token: string;
  employee?: {
    id: number;
    display_name: string;
    role_type: 'ADMIN' | 'STAFF' | 'CAST';
    stage_name?: string | null;
    is_admin?: boolean;
    is_staff?: boolean;
    is_cast?: boolean;
  } | null;
  // Backend sends snake_case 'role_info'; frontend normalizes to 'roleInfo'
  roleInfo?: {
    role_type: 'ADMIN' | 'STAFF' | 'CAST';
    stage_name?: string | null;
    display_name: string;
    is_admin: boolean;
    is_staff: boolean;
    is_cast: boolean;
  } | null;
  dashboard_path?: string;
}

// ページネーション用の型
export interface PaginatedResponse<T> {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

// APIエンドポイントの型定義
export interface ApiEndpoints {
  // テスト用
  test: string;

  // 認証関連
  auth: {
    register: string;
    login: string;
    logout: string;
    switchCompany: string;
    resendVerification: string;
    masterCodes: string;
    setup: string;
    checkStatus: string;
    forgotPassword: string;
    resetPassword: string;
    invitations?: {
      create: string;
      accept: string;
    };
    line?: {
      state: string;
      callback: string;
      link: string;
      unlink: string;
    };
    email?: {
      setup: string;
    };
  };

  // 住所関連
  address: {
    prefectures: string;
    postalCode: string;
  };

  // ユーザー情報
  user: string;
  users: string;


  // 設定関連
  settings: {
    index: string;
    update: string;
  };

  // 通知関連
  notifications: {
    index: string;
    markAsRead: string;
    destroy: string;
    unreadCount: string;
  };

  // ダッシュボード関連
  dashboard: {
    stats: string;
    activities: string;
    clockIn: string;
    clockOut: string;
    breakStart: string;
    breakEnd: string;
    breaks: string;
    nightShiftStart: string;
    nightShiftEnd: string;
    nightShiftInfo: string;
    storeTodayCasts?: (storeId: string | number) => string;
  };

  // 従業員管理関連
  employees: {
    index: string;
    store: string;
    show: (id: string | number) => string;
    update: (id: string | number) => string;
    destroy: (id: string | number) => string;
    restore: (id: string | number) => string;
    retirementCheck: (id: string | number) => string;
    search: string;
    import: string;
    export: string;
  };

  // 従業員配置管理関連
  employeeAssignments: {
    index: string;
    store: string;
    show: (id: string | number) => string;
    update: (id: string | number) => string;
    destroy: (id: string | number) => string;
    getCurrentAssignments: (employeeId: string | number) => string;
    getAssignmentsByOrganization: string;
  };

  // 残業申請管理関連
  overtimeRequests: {
    index: string;
    store: string;
    show: (id: string | number) => string;
    update: (id: string | number) => string;
    destroy: (id: string | number) => string;
    approve: (id: string | number) => string;
    reject: (id: string | number) => string;
    getEmployeeHistory: (employeeId: string | number) => string;
    getPendingRequests: string;
    getStatistics: string;
  };

  // 会社情報管理関連
  company: {
    getInfo: string;
    updateInfo: string;
    updateOrganizationSettings: string;
    getSettings: string;
    updateSettings: string;
  };

  // SaaS 課金プラン関連 (来店会計の plan 系とは別物)
  saas: {
    plan: string;
    features: string;
  };

  // ランク関連
  ranks: {
    index: string;
    store: string;
    update: (id: string | number) => string;
    destroy: (id: string | number) => string;
  };

  // 事業所管理関連
  offices: {
    index: string;
    store: string;
    show: (id: string | number) => string;
    update: (id: string | number) => string;
    destroy: (id: string | number) => string;
  };

  // 店舗管理関連
  stores: {
    index: string;
    store: string;
    show: (id: string | number) => string;
    tablesIndex?: (id: string | number) => string;
    tablesSave?: (id: string | number) => string;
    tablesStatus?: (id: string | number) => string;
    /** キャスト本人の成績表（今月の指名・同伴・出勤・確定バックと自分の過去。2026-11-02） */
    castReportCard?: (id: string | number) => string;
    visitsIndex?: (id: string | number) => string;
    visitsActive?: (id: string | number) => string;
    visitsUnclosed?: (id: string | number) => string;
    businessDaysCurrent?: (id: string | number) => string;
    businessDaysPendingSummary?: (id: string | number, businessDate: string) => string;
    businessDaysSummary?: (id: string | number, businessDate: string) => string;
    businessDaysOpen?: (id: string | number, businessDate: string) => string;
    businessDaysClose?: (id: string | number, businessDate: string) => string;
    businessDaysLock?: (id: string | number, businessDate: string) => string;
    businessDaysReopenLock?: (storeId: string | number, businessDayId: string | number) => string;
    visitsShow?: (storeId: string | number, visitId: string | number) => string;
    visitsAssignCast?: (storeId: string | number, visitId: string | number) => string;
    visitsUnassignCast?: (storeId: string | number, visitId: string | number) => string;
    visitsRemoveCasts?: (storeId: string | number, visitId: string | number) => string;
    visitsSetCustomerType?: (storeId: string | number, visitId: string | number) => string;
    visitsStartServing?: (storeId: string | number, visitId: string | number) => string;
    visitsStartCheckout?: (storeId: string | number, visitId: string | number) => string;
    visitsBackToServing?: (storeId: string | number, visitId: string | number) => string;
    // 伝票の取消（請求 0 円で閉じる・2026-10-30）
    visitsCancel?: (storeId: string | number, visitId: string | number) => string;
    visitsCancelPreview?: (storeId: string | number, visitId: string | number) => string;
    visitsExtendTime?: (storeId: string | number, visitId: string | number) => string;
    // Phase 2: 延長リクエスト（キャスト提案 → スタッフ承認）
    extensionRequestsStore?: (storeId: string | number, visitId: string | number) => string;
    extensionRequestsIndex?: (storeId: string | number) => string;
    extensionRequestsPendingCount?: (storeId: string | number) => string;
    extensionRequestsApprove?: (storeId: string | number, requestId: string | number) => string;
    extensionRequestsReject?: (storeId: string | number, requestId: string | number) => string;
    visitsTablesAttach?: (storeId: string | number, visitId: string | number) => string;
    visitsTablesDetach?: (storeId: string | number, visitId: string | number) => string;
    visitsTablesMove?: (storeId: string | number, visitId: string | number) => string;
    visitsBoardSnapshot?: (id: string | number) => string;
    deductionsIndex?: (id: string | number) => string;
    deductionsUpdate?: (id: string | number, deductionId: string | number) => string;
    deductionsDelete?: (id: string | number, deductionId: string | number) => string;
    setPricingRulesIndex?: (storeId: string | number) => string;
    setPricingRulesStore?: (storeId: string | number) => string;
    setPricingRulesUpdate?: (storeId: string | number, ruleId: string | number) => string;
    setPricingRulesDestroy?: (storeId: string | number, ruleId: string | number) => string;
    update: (id: string | number) => string;
    destroy: (id: string | number) => string;
  };

  // メール認証
  emailVerification: {
    verify: (id: string | number, hash: string) => string;
  };

  // バック管理
  back: {
    policies: {
      index: string;
      /** 複数キャストの「いま当たっているポリシー」をまとめて引く（管理者限定） */
      applicable: string;
      store: string;
      show: (id: string | number) => string;
      update: (id: string | number) => string;
      destroy: (id: string | number) => string;
      duplicate: (id: string | number) => string;
      /** このポリシーがいま当たっているキャスト（ポリシー画面の逆引き・管理者限定） */
      applicableCasts: (id: string | number) => string;
      /** 保存前プレビュー（この内容で保存すると誰に当たるか・管理者限定） */
      preview: string;
      /** 使われたポリシーを止める（有効期間の終わりを今月末にする・管理者限定） */
      endAtMonthEnd: (id: string | number) => string;
    };
    transactions: {
      index: string;
    };
    payrolls: {
      index: string;
    };
    calculate: string;
    commit: string;
  };

  // 勤怠
  attendance: {
    index: string;
    clockIn: string;
    clockOut: string;
    breakStart: string;
    breakEnd: (breakId: string | number) => string;
    statistics: string;
    weeklyStats: string;
    monthlyStats: string;
    onDuty: string;
    forceClockOut: (attendanceId: string | number) => string;
    update: (attendanceId: string | number) => string;
  };


  // メニュー
  menu: {
    items: string;
    show: (id: string | number) => string;
  };

  // オーダー
  orders: {
    index: string;
    store: string;
    show: (id: string | number) => string;
    update: (id: string | number) => string;
    destroy: (id: string | number) => string;
    allocations: (id: string | number) => string;
    feed: (storeId: string | number) => string;
  };

  // メニューアイテム
  menuItems: {
    index: string;
    store: string;
    show: (id: string | number) => string;
    update: (id: string | number) => string;
    destroy: (id: string | number) => string;
    image: (id: string | number) => string;
    storeOverrides: (storeId: string | number, id: string | number) => string;
  };

  // 顧客管理
  customers: {
    index: string;
    store: string;
    show: (id: string | number) => string;
    update: (id: string | number) => string;
    destroy: (id: string | number) => string;
  };

  // 来店受付
  visits: {
    index: string;
    store: string;
    show: (id: string | number) => string;
    update: (id: string | number) => string;
    destroy: (id: string | number) => string;
    assignCast: (id: string | number) => string;
    unassignCast: (id: string | number) => string;
    unclosed: string;
  };

  // 会計処理
  accounting: {
    checkout: string;
    receipts: string;
    salesReport: string;
  };

  // データエクスポート (CSV)
  exports: {
    sales: string;
    payrolls: string;
    attendances: string;
    backs: string;
  };

  // 通知設定
  notificationSettings: {
    index: string;
    methods: string;
  };

  // シフトタイプ
  shiftTypes: {
    index: string;
    store: string;
    show: string;
    update: string;
    destroy: string;
  };

  // 週単位シフト申請
  weeklyShiftRequests: {
    index: string;
    store: string;
    show: string;
    update: string;
    destroy: string;
    submit: string;
    decisions: (id: string | number) => string;
  };

  // 日別シフト判断（Decision）
  dailyShiftDecisions: {
    index: string;
    show: (id: string | number) => string;
    update: (id: string | number) => string;
    bulk: string;
  };
  // シフトテンプレート
  shiftTemplates: {
    index: string;
    store: string;
    show: string;
    update: string;
    destroy: string;
    shiftTypeExamples: string;
    active: string;
  };
  
  // プロフィール管理
  profile: {
    show: string;
    update: string;
    uploadAvatar: string;
    employeeProfile: string;
    updateEmployeeProfile: string;
    /** 🔴 実ルートは `PUT /api/profile/password`。同じ住所に別名の定数を増やさないこと（下の実体のコメント参照） */
    updatePassword: string;
    deleteAvatar: string;
  };

  // 旧・日次サービス（cast_daily_services の固定列）。
  // 2026-08-25 に控除は cast_deductions（行モデル）へ移行し、書き込みは 410。
  // 参照は移行データの突合・監査のためだけに残っている。
  dailyServices: {
    index: (castId: string | number) => string;
  };

  // (旧: 任意控除プレビュー/転記 deductions は 2026-05-31 削除済。
  //  PayrollCalculationJob が控除を自動計算するため不要)

  // 任意控除マスター
  deductionsMaster: {
    index: string;
    store: string;
    update: (id: string | number) => string;
    destroy: (id: string | number) => string;
  };

  // 操作ログ
  operationLogs: {
    index: string;
    store: string;
    archived: string;
    search: string;
    anomalies: string;
    analysis: {
      summary: string;
      frequencyByHour: string;
      frequencyByDayOfWeek: string;
      countByType: string;
      userBehavior: string;
      performance: string;
      operatorStatistics: string;
    };
  };
}

// APIエンドポイントの定数
export const API_ENDPOINTS: ApiEndpoints = {
  // テスト用
  test: `${API_BASE_URL}/test`,

  // 認証関連
  auth: {
    register: `${API_BASE_URL}/auth/register`,
    login: `${API_BASE_URL}/auth/login`,
    logout: `${API_BASE_URL}/auth/logout`,
    // 別の会社（所属しているお店）へ切り替える（2026-10-17）
    switchCompany: `${API_BASE_URL}/auth/switch-company`,
    resendVerification: `${API_BASE_URL}/auth/resend-verification`,
    masterCodes: `${API_BASE_URL}/auth/master-codes`,
    setup: `${API_BASE_URL}/auth/setup`,
    checkStatus: `${API_BASE_URL}/auth/check-status`,
    forgotPassword: `${API_BASE_URL}/auth/forgot-password`,
    resetPassword: `${API_BASE_URL}/auth/reset-password`,
    invitations: {
      create: `${API_BASE_URL}/auth/invitations`,
      accept: `${API_BASE_URL}/auth/invitations/accept`,
    },
    line: {
      state: `${API_BASE_URL}/auth/line/state`,
      callback: `${API_BASE_URL}/auth/line/callback`,
      link: `${API_BASE_URL}/auth/line/link`,
      unlink: `${API_BASE_URL}/auth/line/unlink`,
    },
    email: {
      setup: `${API_BASE_URL}/auth/email/setup`,
    },
  },

  // 住所関連
  address: {
    prefectures: `${API_BASE_URL}/address/prefectures`,
    postalCode: `${API_BASE_URL}/address/postal-code`,
  },

  // ユーザー情報
  user: `${API_BASE_URL}/user`,
  users: `${API_BASE_URL}/users`,


  // 設定関連
  settings: {
    index: `${API_BASE_URL}/settings`,
    update: `${API_BASE_URL}/settings`,
  },

  // 通知関連
  notifications: {
    index: `${API_BASE_URL}/notifications`,
    markAsRead: `${API_BASE_URL}/notifications/mark-as-read`,
    destroy: `${API_BASE_URL}/notifications`,
    unreadCount: `${API_BASE_URL}/notifications/unread-count`,
  },

  // ダッシュボード関連
  dashboard: {
    stats: `${API_BASE_URL}/dashboard/stats`,
    activities: `${API_BASE_URL}/dashboard/activities`,
    clockIn: `${API_BASE_URL}/dashboard/clock-in`,
    clockOut: `${API_BASE_URL}/dashboard/clock-out`,
    breakStart: `${API_BASE_URL}/dashboard/break-start`,
    breakEnd: `${API_BASE_URL}/dashboard/break-end`,
    breaks: `${API_BASE_URL}/dashboard/breaks`,
    nightShiftStart: `${API_BASE_URL}/dashboard/night-shift-start`,
    nightShiftEnd: `${API_BASE_URL}/dashboard/night-shift-end`,
    nightShiftInfo: `${API_BASE_URL}/dashboard/night-shift-info`,
    storeTodayCasts: (storeId) => `${API_BASE_URL}/dashboard/store/${storeId}/today-casts`,
  },

  // 従業員管理関連
  employees: {
    index: `${API_BASE_URL}/employees`,
    store: `${API_BASE_URL}/employees`,
    show: (id) => `${API_BASE_URL}/employees/${id}`,
    update: (id) => `${API_BASE_URL}/employees/${id}`,
    destroy: (id) => `${API_BASE_URL}/employees/${id}`,
    restore: (id) => `${API_BASE_URL}/employees/${id}/restore`,
    retirementCheck: (id) => `${API_BASE_URL}/employees/${id}/retirement-check`,
    search: `${API_BASE_URL}/employees/search`,
    import: `${API_BASE_URL}/employees/import`,
    export: `${API_BASE_URL}/employees/export`,
  },

  // 従業員配置管理関連
  employeeAssignments: {
    index: `${API_BASE_URL}/employee-assignments`,
    store: `${API_BASE_URL}/employee-assignments`,
    show: (id) => `${API_BASE_URL}/employee-assignments/${id}`,
    update: (id) => `${API_BASE_URL}/employee-assignments/${id}`,
    destroy: (id) => `${API_BASE_URL}/employee-assignments/${id}`,
    getCurrentAssignments: (employeeId) => `${API_BASE_URL}/employee-assignments/employee/${employeeId}/current`,
    getAssignmentsByOrganization: `${API_BASE_URL}/employee-assignments/organization`,
  },

  // 残業申請管理関連
  overtimeRequests: {
    index: `${API_BASE_URL}/overtime-requests`,
    store: `${API_BASE_URL}/overtime-requests`,
    show: (id) => `${API_BASE_URL}/overtime-requests/${id}`,
    update: (id) => `${API_BASE_URL}/overtime-requests/${id}`,
    destroy: (id) => `${API_BASE_URL}/overtime-requests/${id}`,
    approve: (id) => `${API_BASE_URL}/overtime-requests/${id}/approve`,
    reject: (id) => `${API_BASE_URL}/overtime-requests/${id}/reject`,
    getEmployeeHistory: (employeeId) => `${API_BASE_URL}/overtime-requests/employee/${employeeId}/history`,
    getPendingRequests: `${API_BASE_URL}/overtime-requests/pending`,
    getStatistics: `${API_BASE_URL}/overtime-requests/statistics`,
  },

  // 会社情報管理関連
  company: {
    getInfo: `${API_BASE_URL}/company/info`,
    updateInfo: `${API_BASE_URL}/company/info`,
    updateOrganizationSettings: `${API_BASE_URL}/company/organization-settings`,
    getSettings: `${API_BASE_URL}/company/settings`,
    updateSettings: `${API_BASE_URL}/company/settings`,
  },

  // SaaS 課金プラン関連 (plan は会社のオーナー限定・features は誰でも)
  saas: {
    plan: `${API_BASE_URL}/saas/plan`,
    features: `${API_BASE_URL}/saas/features`,
  },


  // 事業所管理関連
  offices: {
    index: `${API_BASE_URL}/offices`,
    store: `${API_BASE_URL}/offices`,
    show: (id) => `${API_BASE_URL}/offices/${id}`,
    update: (id) => `${API_BASE_URL}/offices/${id}`,
    destroy: (id) => `${API_BASE_URL}/offices/${id}`,
  },

  // 店舗管理関連
  stores: {
    index: `${API_BASE_URL}/stores`,
    store: `${API_BASE_URL}/stores`,
    show: (id) => `${API_BASE_URL}/stores/${id}`,
    tablesIndex: (id) => `${API_BASE_URL}/stores/${id}/tables`,
    tablesSave: (id) => `${API_BASE_URL}/stores/${id}/tables`,
    tablesStatus: (id) => `${API_BASE_URL}/stores/${id}/tables/status`,
    castReportCard: (id) => `${API_BASE_URL}/stores/${id}/cast/report-card`,
    visitsIndex: (id) => `${API_BASE_URL}/stores/${id}/visits`,
    visitsActive: (id) => `${API_BASE_URL}/stores/${id}/visits/active`,
    visitsUnclosed: (id) => `${API_BASE_URL}/stores/${id}/visits/unclosed`,
    businessDaysCurrent: (id) => `${API_BASE_URL}/stores/${id}/business-days/current`,
    businessDaysPendingSummary: (id, businessDate) => `${API_BASE_URL}/stores/${id}/business-days/${businessDate}/pending-summary`,
    businessDaysSummary: (id, businessDate) => `${API_BASE_URL}/stores/${id}/business-days/${businessDate}/summary`,
    businessDaysOpen: (id, businessDate) => `${API_BASE_URL}/stores/${id}/business-days/${businessDate}/open`,
    businessDaysClose: (id, businessDate) => `${API_BASE_URL}/stores/${id}/business-days/${businessDate}/close`,
    businessDaysLock: (id, businessDate) => `${API_BASE_URL}/stores/${id}/business-days/${businessDate}/lock`,
    // 締め解除（locked → closed）。店舗スコープルート（旧 /store-business-days/{id}/... はルート未定義で404だった）
    businessDaysReopenLock: (storeId, businessDayId) => `${API_BASE_URL}/stores/${storeId}/business-days/${businessDayId}/reopen-lock`,
    visitsShow: (storeId, visitId) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}`,
    visitsAssignCast: (storeId, visitId) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/assign-cast`,
    visitsUnassignCast: (storeId, visitId) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/unassign-cast`,
    visitsRemoveCasts: (storeId: string | number, visitId: string | number) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/remove-casts`,
    // 顧客タイプ (free/called) を手動トグル
    visitsSetCustomerType: (storeId: string | number, visitId: string | number) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/customer-type`,
    visitsStartServing: (storeId, visitId) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/start-serving`,
    visitsStartCheckout: (storeId, visitId) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/start-checkout`,
    visitsBackToServing: (storeId, visitId) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/back-to-serving`,
    visitsCancel: (storeId, visitId) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/cancel`,
    visitsCancelPreview: (storeId, visitId) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/cancel-preview`,
    visitsExtendTime: (storeId, visitId) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/extend-time`,
    // Phase 2: 延長リクエスト
    extensionRequestsStore: (storeId, visitId) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/extension-requests`,
    extensionRequestsIndex: (storeId) => `${API_BASE_URL}/stores/${storeId}/extension-requests`,
    extensionRequestsPendingCount: (storeId) => `${API_BASE_URL}/stores/${storeId}/extension-requests/pending-count`,
    extensionRequestsApprove: (storeId, requestId) => `${API_BASE_URL}/stores/${storeId}/extension-requests/${requestId}/approve`,
    extensionRequestsReject: (storeId, requestId) => `${API_BASE_URL}/stores/${storeId}/extension-requests/${requestId}/reject`,
    visitsTablesAttach: (storeId, visitId) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/tables/attach`,
    visitsTablesDetach: (storeId, visitId) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/tables/detach`,
    visitsTablesMove: (storeId, visitId) => `${API_BASE_URL}/stores/${storeId}/visits/${visitId}/tables/move`,
    visitsBoardSnapshot: (id) => `${API_BASE_URL}/stores/${id}/visits/board-snapshot`,
    // 店舗別の控除設定（金額の上書き）。控除の「記録」は /cast-deductions 側。
    deductionsIndex: (id) => `${API_BASE_URL}/stores/${id}/deductions`,
    deductionsUpdate: (id, deductionId) => `${API_BASE_URL}/stores/${id}/deductions/${deductionId}`,
    deductionsDelete: (id, deductionId) => `${API_BASE_URL}/stores/${id}/deductions/${deductionId}`,
    setPricingRulesIndex: (storeId) => `${API_BASE_URL}/stores/${storeId}/set-pricing-rules`,
    setPricingRulesStore: (storeId) => `${API_BASE_URL}/stores/${storeId}/set-pricing-rules`,
    setPricingRulesUpdate: (storeId, ruleId) => `${API_BASE_URL}/stores/${storeId}/set-pricing-rules/${ruleId}`,
    setPricingRulesDestroy: (storeId, ruleId) => `${API_BASE_URL}/stores/${storeId}/set-pricing-rules/${ruleId}`,
    update: (id) => `${API_BASE_URL}/stores/${id}`,
    destroy: (id) => `${API_BASE_URL}/stores/${id}`,
  },

  // メール認証
  emailVerification: {
    verify: (id, hash) => `${API_BASE_URL}/email/verify/${id}/${hash}`,
  },

  // バック管理
  back: {
    policies: {
      index: `${API_BASE_URL}/back/policies`,
      applicable: `${API_BASE_URL}/back/policies/applicable`,
      store: `${API_BASE_URL}/back/policies`,
      show: (id) => `${API_BASE_URL}/back/policies/${id}`,
      update: (id) => `${API_BASE_URL}/back/policies/${id}`,
      destroy: (id) => `${API_BASE_URL}/back/policies/${id}`,
      duplicate: (id) => `${API_BASE_URL}/back/policies/${id}/duplicate`,
      applicableCasts: (id) => `${API_BASE_URL}/back/policies/${id}/applicable-casts`,
      preview: `${API_BASE_URL}/back/policies/preview`,
      endAtMonthEnd: (id) => `${API_BASE_URL}/back/policies/${id}/end-at-month-end`,
    },
    transactions: {
      index: `${API_BASE_URL}/back/transactions`,
    },
    payrolls: {
      index: `${API_BASE_URL}/back/payrolls`,
    },
    calculate: `${API_BASE_URL}/back/calculate`,
    commit: `${API_BASE_URL}/back/commit`,
  },

  // 旧・日次サービス（参照のみ。書き込みは 410 で塞いだ）
  dailyServices: {
    index: (castId) => `${API_BASE_URL}/casts/${castId}/daily-services`,
  },

  // (旧手動転記 API deductions.preview / deductions.materialize は
  //  給与計算 (PayrollCalculationJob::buildDeductionItems) へ統合済)

  // 任意控除マスター
  deductionsMaster: {
    index: `${API_BASE_URL}/deductions-master`,
    store: `${API_BASE_URL}/deductions-master`,
    update: (id) => `${API_BASE_URL}/deductions-master/${id}`,
    destroy: (id) => `${API_BASE_URL}/deductions-master/${id}`,
  },

  // 勤怠
  attendance: {
    index: `${API_BASE_URL}/attendance`,
    clockIn: `${API_BASE_URL}/attendance/clock-in`,
    clockOut: `${API_BASE_URL}/attendance/clock-out`,
    breakStart: `${API_BASE_URL}/attendance/breaks/start`,
    breakEnd: (breakId) => `${API_BASE_URL}/attendance/breaks/${breakId}/end`,
    statistics: `${API_BASE_URL}/attendance/statistics`,
    weeklyStats: `${API_BASE_URL}/attendance/weekly-stats`,
    monthlyStats: `${API_BASE_URL}/attendance/monthly-stats`,
    onDuty: `${API_BASE_URL}/attendance/on-duty`,
    forceClockOut: (attendanceId) => `${API_BASE_URL}/attendance/${attendanceId}/force-clock-out`,
    update: (attendanceId) => `${API_BASE_URL}/attendance/${attendanceId}`,
  },

  // ランク
  ranks: {
    index: `${API_BASE_URL}/ranks`,
    store: `${API_BASE_URL}/ranks`,
    update: (id) => `${API_BASE_URL}/ranks/${id}`,
    destroy: (id) => `${API_BASE_URL}/ranks/${id}`,
  },


  // メニュー
  menu: {
    items: `${API_BASE_URL}/menu/items`,
    show: (id) => `${API_BASE_URL}/menu/items/${id}`,
  },

  // オーダー
  orders: {
    index: `${API_BASE_URL}/orders`,
    store: `${API_BASE_URL}/orders`,
    show: (id) => `${API_BASE_URL}/orders/${id}`,
    update: (id) => `${API_BASE_URL}/orders/${id}`,
    destroy: (id) => `${API_BASE_URL}/orders/${id}`,
    allocations: (id) => `${API_BASE_URL}/orders/${id}/allocations`,
    // 新着オーダー（キャストが入れたオーダーを黒服の端末に知らせる。顧客要望台帳 #24）。
    // 店舗は URL で渡す（X-Store-ID のフラットルートだと 5 秒ごとに LEGACY_ROUTE_ACCESSED の WARNING が出る）
    feed: (storeId) => `${API_BASE_URL}/stores/${storeId}/orders/feed`,
  },

  // メニューアイテム
  menuItems: {
    index: `${API_BASE_URL}/menu-items`,
    store: `${API_BASE_URL}/menu-items`,
    show: (id) => `${API_BASE_URL}/menu-items/${id}`,
    update: (id) => `${API_BASE_URL}/menu-items/${id}`,
    destroy: (id) => `${API_BASE_URL}/menu-items/${id}`,
    // メニュー画像 (会社共通) のアップロード/削除
    image: (id: string | number) => `${API_BASE_URL}/menu-items/${id}/image`,
    // 新ルート: stores/{storeId}/menu-items/{id}/store-overrides
    storeOverrides: (storeId: string | number, id: string | number) => `${API_BASE_URL}/stores/${storeId}/menu-items/${id}/store-overrides`,
  },

  // 顧客管理
  customers: {
    index: `${API_BASE_URL}/customers`,
    store: `${API_BASE_URL}/customers`,
    show: (id) => `${API_BASE_URL}/customers/${id}`,
    update: (id) => `${API_BASE_URL}/customers/${id}`,
    destroy: (id) => `${API_BASE_URL}/customers/${id}`,
  },

  // 来店受付
  visits: {
    index: `${API_BASE_URL}/visits`,
    store: `${API_BASE_URL}/visits`,
    show: (id) => `${API_BASE_URL}/visits/${id}`,
    update: (id) => `${API_BASE_URL}/visits/${id}`,
    destroy: (id) => `${API_BASE_URL}/visits/${id}`,
    assignCast: (id) => `${API_BASE_URL}/visits/${id}/assign-cast`,
    unassignCast: (id) => `${API_BASE_URL}/visits/${id}/unassign-cast`,
    unclosed: `${API_BASE_URL}/visits/unclosed`,
  },

  // 会計処理
  accounting: {
    checkout: `${API_BASE_URL}/accounting/checkout`,
    receipts: `${API_BASE_URL}/accounting/receipts`,
    salesReport: `${API_BASE_URL}/accounting/sales-report`,
  },

  // データエクスポート (CSV)
  exports: {
    sales: `${API_BASE_URL}/exports/sales.csv`,
    payrolls: `${API_BASE_URL}/exports/payrolls.csv`,
    attendances: `${API_BASE_URL}/exports/attendances.csv`,
    backs: `${API_BASE_URL}/exports/backs.csv`,
  },

  // 通知設定
  notificationSettings: {
    index: `${API_BASE_URL}/notification-settings`,
    methods: `${API_BASE_URL}/notification-settings/methods`,
  },

  // シフトタイプ
  shiftTypes: {
    index: `${API_BASE_URL}/shift-types`,
    store: `${API_BASE_URL}/shift-types`,
    show: `${API_BASE_URL}/shift-types/:id`,
    update: `${API_BASE_URL}/shift-types/:id`,
    destroy: `${API_BASE_URL}/shift-types/:id`,
  },

  // 週単位シフト申請
  weeklyShiftRequests: {
    index: `${API_BASE_URL}/weekly-shift-requests`,
    store: `${API_BASE_URL}/weekly-shift-requests`,
    show: `${API_BASE_URL}/weekly-shift-requests/:id`,
    update: `${API_BASE_URL}/weekly-shift-requests/:id`,
    destroy: `${API_BASE_URL}/weekly-shift-requests/:id`,
    submit: `${API_BASE_URL}/weekly-shift-requests/:id/submit`,
    decisions: (id) => `${API_BASE_URL}/weekly-shift-requests/${id}/decisions`,
  },

  // 日別シフト判断（Decision）
  dailyShiftDecisions: {
    index: `${API_BASE_URL}/daily-shift-decisions`,
    show: (id) => `${API_BASE_URL}/daily-shift-decisions/${id}`,
    update: (id) => `${API_BASE_URL}/daily-shift-decisions/${id}`,
    bulk: `${API_BASE_URL}/daily-shift-decisions/bulk`,
  },

  // シフトテンプレート
  shiftTemplates: {
    index: `${API_BASE_URL}/shift-templates`,
    store: `${API_BASE_URL}/shift-templates`,
    show: `${API_BASE_URL}/shift-templates/:id`,
    update: `${API_BASE_URL}/shift-templates/:id`,
    destroy: `${API_BASE_URL}/shift-templates/:id`,
    shiftTypeExamples: `${API_BASE_URL}/shift-templates/shift-type-examples`,
    active: `${API_BASE_URL}/shift-templates/active`,
  },

  // プロフィール管理
  profile: {
    show: `${API_BASE_URL}/profile`,
    update: `${API_BASE_URL}/profile`,
    uploadAvatar: `${API_BASE_URL}/profile/avatar`,
    employeeProfile: `${API_BASE_URL}/profile/employee/:id`,
    updateEmployeeProfile: `${API_BASE_URL}/profile/employee/:id`,
    // 🔴 実ルートは `PUT /api/profile/password`（routes/api.php）。
    //    ここが `/profile/update-password` になっていて、**パスワード変更が全画面で 404**
    //    だった（`/profile` と `/settings` の両方が profileAPI.updatePassword を呼ぶ。
    //    画面には「うまくいきませんでした」としか出ないので、本番の導入作業で踏むまで誰も気づけなかった）。
    //    同じ住所に `changePassword` という**使われていない正しい定数**が並んでいたのが元凶なので、
    //    重複は消して 1 つにした（2026-10-12）。
    updatePassword: `${API_BASE_URL}/profile/password`,
    // 実ルートは `DELETE /api/profile/avatar`。画面からは未使用だが、同じ踏み方をしないよう揃える。
    deleteAvatar: `${API_BASE_URL}/profile/avatar`,
  },

  // 操作ログ
  operationLogs: {
    index: `${API_BASE_URL}/operation-logs`,
    store: `${API_BASE_URL}/operation-logs`,
    archived: `${API_BASE_URL}/operation-logs/archived`,
    search: `${API_BASE_URL}/operation-logs/search`,
    anomalies: `${API_BASE_URL}/operation-logs/anomalies`,
    analysis: {
      summary: `${API_BASE_URL}/operation-logs/analysis/summary`,
      frequencyByHour: `${API_BASE_URL}/operation-logs/analysis/frequency-by-hour`,
      frequencyByDayOfWeek: `${API_BASE_URL}/operation-logs/analysis/frequency-by-day-of-week`,
      countByType: `${API_BASE_URL}/operation-logs/analysis/count-by-type`,
      userBehavior: `${API_BASE_URL}/operation-logs/analysis/user-behavior`,
      performance: `${API_BASE_URL}/operation-logs/analysis/performance`,
      operatorStatistics: `${API_BASE_URL}/operation-logs/analysis/operator-statistics`,
    },
  },
};

// 操作ログ関連の型定義
export interface OperationLog {
  id: string;
  timestamp: number;
  type: string;
  operator_name: string;
  message: string;
  table_nos?: string[] | null;
  category?: 'admin' | 'business' | 'payroll';
  company_id?: number;
  store_id?: number;
  business_date?: string;
  severity?: 'info' | 'warning' | 'error' | 'critical';
  visit_id?: number | null;
  order_id?: number | null;
  customer_id?: number | null;
  item_id?: string | null;
  amount?: number | null;
  before_values?: Record<string, any> | null;
  after_values?: Record<string, any> | null;
  changed_fields?: string[] | null;
  screen?: string | null;
  feature?: string | null;
  metadata?: Record<string, any> | null;
  tags?: string[] | null;
  created_at?: string;
  updated_at?: string;

  // 監査ログ画面で使用する追加フィールド
  operator_id?: number | null;
  is_anomaly?: boolean | null;
  risk_level?: 'low' | 'medium' | 'high' | 'critical' | null;
  anomaly_score?: number | null;
  anomaly_reasons?: string[] | null;
  ip_address?: string | null;
  device_type?: string | null;
}

export interface OperationLogCreateRequest {
  type: string;
  category?: 'admin' | 'business' | 'payroll';
  severity?: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  table_nos?: string[] | null;
  visit_id?: number | null;
  order_id?: number | null;
  customer_id?: number | null;
  item_id?: string | null;
  amount?: number | null;
  before_values?: Record<string, any> | null;
  after_values?: Record<string, any> | null;
  changed_fields?: string[] | null;
  screen?: string | null;
  feature?: string | null;
  metadata?: Record<string, any> | null;
  tags?: string[] | null;
  company_id: number;
  store_id: number;
  /**
   * 省略可。 未指定ならサーバ側が店舗の cutoff_time で営業日を解決する
   * (FE の getBusinessDate() は cutoff 05:00 固定のため、 原則は送らない)。
   */
  business_date?: string;
}

export interface OperationLogListResponse extends ApiResponse<OperationLog[]> {
  data: OperationLog[];
  // ページネーション情報（サーバー側のmetaに合わせて拡張）
  meta?: {
    total?: number;
    per_page?: number;
    current_page?: number;
    last_page?: number;
    /** サーバが店舗の cutoff_time で解決した営業日。 FE の 05:00 固定計算より優先して使う */
    business_date?: string;
  };
}

// ボトル/シャンパンバック配分 (memory: project_back_allocations_phase2)
// shape は BackAllocationController@index の返却に一致させる
export interface BottleAllocation {
  cast_id: number;
  allocated_amount: number;
  allocation_method: string | null;
  cast_name: string | null;
  is_frozen: boolean;
}

export interface BottleAllocationCandidate {
  cast_id: number;
  cast_name: string | null;
  role: string; // 'bottle'(ボトルを付けたキャスト=既定受取人) | 'primary' | nomination_type | 'none'
}

export interface BottleAllocationStoreCast {
  cast_id: number;
  cast_name: string | null;
}

export interface BottleAllocationData {
  order_line_id: number;
  is_bottle: boolean;
  total_back_amount: number;
  frozen_total: number;
  allocations: BottleAllocation[];
  candidate_casts: BottleAllocationCandidate[];
  store_casts: BottleAllocationStoreCast[];
}

export type BottleAllocationResponse = ApiResponse<BottleAllocationData>;

// ─── 新着オーダー（GET /api/stores/{storeId}/orders/feed。顧客要望台帳 #24） ───
// キャストが入れたオーダーを黒服の端末に音で知らせるため、スタッフ画面が数秒ごとに取りに行く。

export interface OrderFeedItem {
  id: number;
  visit_id: number;
  /** 伝票の営業日 'YYYY-MM-DD'（日付をまたぐ営業では暦日と違う） */
  business_date: string | null;
  /** メイン席の表示名（現場の呼び名。未設定なら table_key） */
  table_label: string | null;
  menu_name: string | null;
  /** 明細のメモ。キャストドリンクの中身（`drink_content:ウーロン茶`）が入る（2026-10-11。中身なし・古いサーバでは無い） */
  note?: string | null;
  qty: number;
  /** 誰の一杯か（バックの対象キャスト） */
  cast_name: string | null;
  /** 注文者（入力した人）。システムが作った明細は返らないので常にある */
  ordered_by: { id: number; name: string; is_cast: boolean } | null;
  ordered_at: string | null;
}

export interface OrderFeedResponse {
  /** 次に after_id として渡す値。直近 5 秒の明細はまだ追い越さない（次の取得でもう一度返る＝画面側で重複を落とす） */
  cursor: number;
  items: OrderFeedItem[];
}